import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { folderStructure, msgPickDate, tryDecodeFilePath } from '@/utils/constants';
import { MSG_TYPE } from '@/utils/folders';
import { graphClient } from '@/utils/msGraphClient';
import { authBaseQuery } from '@/features/baseQuery';
import {
	buildBatchRequests,
	buildFolderBatchRequests,
	buildListItemBatchRequests,
	buildUploadSessionBatchRequests,
	convertFileToGraphAttachment,
	encodeSharingUrl,
	escapeODataSearchPhrase,
	escapeODataString,
	getError,
	getSharepointError,
	invalidateFilesTags,
	prepareFileForUpload,
	uploadFileContentToSession,
} from './helper';
import { readMsgFile } from '@/utils/msGraphUtils';
import { chunk } from 'lodash';
import { filesApi } from '../files/filesApi';
import { showToast } from '../toast/toastSlice';
import { safelyParseDate } from '@/utils/date';

const getMetadataWriteErrorDetails = (error) => {
	const status = error?.status;
	const message = error?.message ?? '';

	return {
		status,
		message: String(message),
	};
};

const parseListItemMetadata = (listItem) => {
	const url = listItem?.webUrl ? listItem.webUrl.split('/sites/') : [''];
	const serverRelativeUrl = `/sites/${url[url.length - 1]}`;
	return {
		description:
			listItem?.fields?._ExtendedDescription ??
			listItem?.fields?.c000__ExtendedDescription ??
			'',
		metadata: listItem?.fields,
		serverRelativeUrl,
	};
};

const getListItem = async ({ drive, driveId, dispatch }) => {
	const listItem = await dispatch(
		msGraphApi.endpoints.getListItem.initiate({
			driveId,
			itemId: drive.id,
		}),
	)
		.unwrap()
		.catch((e) => console.log('Error getting list item', e));
	return {
		...(listItem && {
			listItem,
			...parseListItemMetadata(listItem),
		}),
	};
};

const getDriveMetadata = async ({ drive, driveId, dispatch }) => {
	let metadata = {};
	if (!drive.listItem) {
		metadata = await getListItem({ drive, driveId, dispatch });
	}

	if (['application/vnd.ms-outlook', 'message/rfc822'].includes(drive?.file?.mimeType)) {
		try {
			const endpoint =
				drive.file.mimeType === 'application/vnd.ms-outlook'
					? 'getMsgFileMetadata'
					: 'getEmlFileMetadata';
			const msgMetadata = await dispatch(
				filesApi.endpoints[endpoint].initiate({
					driveId: drive?.parentReference?.id,
					id: drive?.id,
					clientFolderDriveId: drive?.parentReference?.driveId,
				}),
			).unwrap();
			metadata = { ...metadata, ...msgMetadata };
		} catch (err) {
			console.error('Failed to parse MSG file', err);
			const sharepointError = getSharepointError(err);
			if (!sharepointError) {
				return metadata;
			}
			dispatch(
				showToast({
					id: `sharepoint-file-download-error:${sharepointError.errorMessage}`,
					message: sharepointError.errorMessage,
					error: true,
					autohide: false,
				}),
			);
		}
	} else {
		metadata.metadataLoading = false;
	}
	return metadata;
};

const patchGetItemsCachesByItemId = ({
	dispatch,
	getState,
	itemId,
	updater, // (existingItem) => nextItem
}) => {
	const cachedArgs = msGraphApi.util.selectCachedArgsForQuery(getState(), 'getItems');

	for (const queryArg of cachedArgs) {
		dispatch(
			msGraphApi.util.updateQueryData('getItems', queryArg, (draft) =>
				updateItemInGetItemsDraft(draft, itemId, updater),
			),
		);
	}
};

const updateItemInGetItemsDraft = (draft, itemId, updater) => {
	for (const page of draft.pages ?? []) {
		for (const collection of ['files', 'folders']) {
			const index = page[collection]?.findIndex((item) => item.id === itemId) ?? -1;
			if (index === -1) continue;

			page[collection][index] = updater(page[collection][index]);
		}
	}
};

const GET_ITEMS_PAGE_SIZE = 50;

const matchesGetItemsSearch = (name, { searchText, exact }) => {
	if (!searchText) {
		return true;
	}
	if (exact) {
		return tryDecodeFilePath(name, searchText);
	}
	return name.toLowerCase().includes(searchText.toLowerCase());
};

const normalizeCreatedDriveItem = (driveItem) => {
	const isMsgOrEml = ['application/vnd.ms-outlook', 'message/rfc822'].includes(
		driveItem?.file?.mimeType,
	);

	return {
		...driveItem,
		...(isMsgOrEml && { metadataLoading: true }),
	};
};

const flattenGetItemsPage = (page) => [...(page?.files ?? []), ...(page?.folders ?? [])];

const splitPageItems = (items) => ({
	files: items.filter((item) => Object.keys(item).includes('file')),
	folders: items.filter((item) => !Object.keys(item).includes('file')),
});

const getTotalCachedItems = (pages) =>
	pages.reduce((sum, page) => sum + flattenGetItemsPage(page).length, 0);

const compareGetItemsItems = (a, b, { orderBy, direction }) => {
	const dir = direction === 'asc' ? 1 : -1;

	if (orderBy === 'name') {
		return dir * (a.name ?? '').localeCompare(b.name ?? '');
	}

	if (orderBy === 'sentDate') {
		const aDate = a.sentDate ? safelyParseDate(a.sentDate).getTime() : 0;
		const bDate = b.sentDate ? safelyParseDate(b.sentDate).getTime() : 0;
		return dir * (aDate - bDate);
	}

	if (orderBy === 'createdBy') {
		const aValue = a.createdBy?.user?.displayName ?? a.uploadedBy ?? '';
		const bValue = b.createdBy?.user?.displayName ?? b.uploadedBy ?? '';
		return dir * aValue.localeCompare(bValue);
	}

	const aTime = Date.parse(a[orderBy] ?? 0);
	const bTime = Date.parse(b[orderBy] ?? 0);
	return dir * (aTime - bTime);
};

const insertCreatedItemsIntoGetItemsDraft = (draft, queryArg, createdItems) => {
	if (!createdItems?.length) {
		return { patched: false, insertedCount: 0 };
	}

	const eligibleItems = createdItems.filter((item) =>
		matchesGetItemsSearch(item.name, queryArg),
	);
	if (!eligibleItems.length) {
		return { patched: false, insertedCount: 0 };
	}

	const normalizedItems = eligibleItems.map(normalizeCreatedDriveItem);

	if (!draft.pages?.length) {
		draft.pages = [{ files: [], folders: [] }];
	}

	const totalCached = getTotalCachedItems(draft.pages);

	if (totalCached === 0) {
		const sorted = [...normalizedItems].sort((a, b) =>
			compareGetItemsItems(a, b, queryArg),
		);
		const pageCount = draft.pages.length;
		const capacity = GET_ITEMS_PAGE_SIZE * pageCount;
		const kept = sorted.slice(0, capacity);

		for (let pageIndex = 0; pageIndex < pageCount; pageIndex += 1) {
			const chunk = kept.slice(
				pageIndex * GET_ITEMS_PAGE_SIZE,
				(pageIndex + 1) * GET_ITEMS_PAGE_SIZE,
			);
			const { files, folders } = splitPageItems(chunk);
			draft.pages[pageIndex] = {
				...draft.pages[pageIndex],
				files,
				folders,
			};
		}

		return { patched: true, insertedCount: kept.length };
	}

	const existingItems = draft.pages.flatMap(flattenGetItemsPage);
	const newIds = new Set(normalizedItems.map((item) => item.id));
	const existingWithoutNew = existingItems.filter((item) => !newIds.has(item.id));

	const itemsToInsert = normalizedItems.filter(
		(item) => !existingItems.some((existing) => existing.id === item.id),
	);

	if (!itemsToInsert.length) {
		return { patched: false, insertedCount: 0 };
	}

	const sorted = [...existingWithoutNew, ...itemsToInsert].sort((a, b) =>
		compareGetItemsItems(a, b, queryArg),
	);
	const pageCount = draft.pages.length;
	const maxCapacity = GET_ITEMS_PAGE_SIZE * pageCount;
	const targetSize = Math.min(totalCached + itemsToInsert.length, sorted.length, maxCapacity);

	if (targetSize <= totalCached) {
		return { patched: false, insertedCount: 0 };
	}

	const kept = sorted.slice(0, targetSize);

	for (let pageIndex = 0; pageIndex < pageCount; pageIndex += 1) {
		const chunk = kept.slice(
			pageIndex * GET_ITEMS_PAGE_SIZE,
			(pageIndex + 1) * GET_ITEMS_PAGE_SIZE,
		);
		const { files, folders } = splitPageItems(chunk);

		draft.pages[pageIndex] = {
			...draft.pages[pageIndex],
			files,
			folders,
		};
	}

	return { patched: true, insertedCount: itemsToInsert.length };
};

const bumpParentFolderChildCount = ({ dispatch, driveId, parentItemId, incrementBy }) => {
	if (!incrementBy) {
		return;
	}

	dispatch(
		msGraphApi.util.updateQueryData(
			'getDriveItem',
			{ driveId, itemId: parentItemId },
			(draft) => {
				if (draft?.folder) {
					draft.folder.childCount = (draft.folder.childCount ?? 0) + incrementBy;
				}
			},
		),
	);
};

const patchGetItemsCachesAfterCreate = ({
	dispatch,
	getState,
	driveId,
	parentItemId,
	createdItems,
}) => {
	const items = (Array.isArray(createdItems) ? createdItems : [createdItems]).filter(Boolean);
	if (!items.length) {
		return false;
	}

	const cachedArgs = msGraphApi.util
		.selectCachedArgsForQuery(getState(), 'getItems')
		.filter((arg) => arg.driveId === driveId && arg.itemId === parentItemId);

	if (!cachedArgs.length) {
		return false;
	}

	let didPatchNonSearch = false;
	const hasNonSearchCache = cachedArgs.some((arg) => !arg.searchText);

	for (const queryArg of cachedArgs) {
		if (queryArg.searchText) {
			continue;
		}

		try {
			dispatch(
				msGraphApi.util.updateQueryData('getItems', queryArg, (draft) => {
					const { patched } = insertCreatedItemsIntoGetItemsDraft(
						draft,
						queryArg,
						items,
					);
					if (patched) {
						didPatchNonSearch = true;
					}
				}),
			);
		} catch (e) {
			console.log('Error patching getItems cache after create', e);
		}
	}

	if (hasNonSearchCache && didPatchNonSearch) {
		try {
			bumpParentFolderChildCount({
				dispatch,
				driveId,
				parentItemId,
				incrementBy: items.length,
			});
		} catch (e) {
			console.log('Error bumping folder childCount', e);
		}
	}

	return didPatchNonSearch;
};

export const invalidateGetItemsForParent = (dispatch, getState, { driveId, itemId }) => {
	if (!driveId || !itemId) {
		return;
	}

	const cachedArgs = msGraphApi.util
		.selectCachedArgsForQuery(getState(), 'getItems')
		.filter((arg) => arg.driveId === driveId && arg.itemId === itemId);

	const tags = cachedArgs.map(({ direction, orderBy, searchText }) => ({
		type: 'ITEMS',
		id: `${driveId}-${itemId}-${direction}-${orderBy}${searchText ? `-${searchText}` : ''}`,
	}));

	if (tags.length) {
		dispatch(msGraphApi.util.invalidateTags(tags));
	}

	dispatch(msGraphApi.util.invalidateTags([{ type: 'ITEM', id: itemId }]));
};

/**
 * When SharePoint rejects a metadata write with a 400 Bad Request, the error message includes the name of the unrecognized field.
 * This function attempts to parse that field name out of the error message so we can remove it from the payload and retry the request.
 * The error message looks like: "Field 'CustomField' is not recognized."
 */
const getUnknownMetadataField = (message = '') => {
	const match = String(message).match(/Field '([^']+)' is not recognized/i);
	return match?.[1] ?? null;
};

/**
 * Attempts to add the provided metadata to the uploaded file.
 * If SharePoint returns an error indicating that a field is unrecognized (which happens when trying to write to a column that hasn't been provisioned in the tenant),
 * the function will remove the unrecognized field and retry until either all metadata is saved or there are no fields left to save.
 */
const addMetadataToUploadedFile = async ({ dispatch, driveId, itemId, metadata = {} }) => {
	const metadataFields = Object.keys(metadata);
	if (metadataFields.length === 0 || !itemId) {
		return;
	}

	// Try to save every metadata property first so any SharePoint columns that already
	// exist on the tenant are populated in one request.
	let metadataToSave = { ...metadata };

	while (Object.keys(metadataToSave).length > 0) {
		try {
			await dispatch(
				msGraphApi.endpoints.addMetadataToFile.initiate({
					driveId,
					itemId,
					metadata: metadataToSave,
				}),
			).unwrap();

			return;
		} catch (error) {
			// If SharePoint rejects the payload, remove the failing property and retry.
			// This lets us keep saving any fields that have already been provisioned.
			const { status, message } = getMetadataWriteErrorDetails(error);
			const isBadRequest = status === 400;

			if (!isBadRequest) {
				return;
			}

			const unknownField = getUnknownMetadataField(message);
			const fallbackField =
				Object.keys(metadataToSave)[Object.keys(metadataToSave).length - 1];
			// Prefer the field named in the Graph error message; otherwise drop one field and retry.
			const fieldToRemove =
				unknownField && Object.prototype.hasOwnProperty.call(metadataToSave, unknownField)
					? unknownField
					: fallbackField;

			if (!fieldToRemove) {
				return;
			}

			const { [fieldToRemove]: _removed, ...remainingMetadata } = metadataToSave;
			metadataToSave = remainingMetadata;
		}
	}
};

export const msGraphApi = createApi({
	reducerPath: 'msGraphApi',
	tagTypes: [
		'ITEMS',
		'ITEM',
		'MSG_FILE',
		'LIST_ITEM',
		'LIST',
		'LISTS',
		'DRIVES',
		'BREADCRUMBS',
		'TASK',
	],
	baseQuery: fakeBaseQuery(),
	endpoints: (builder) => ({
		getItems: builder.infiniteQuery({
			// providesTags: ['ITEMS'],
			providesTags: (result, error, { direction, driveId, itemId, orderBy, searchText }) => {
				return [
					{
						type: 'ITEMS',
						id: `${driveId}-${itemId}-${direction}-${orderBy}${
							searchText ? `-${searchText}` : ''
						}`,
					},
					...(result?.folders ?? []).concat(result?.files ?? []).map((f) => ({
						type: 'ITEM',
						id: f.id,
					})),
					...(result?.files ?? [])
						.filter((f) => f?.file?.mimeType == MSG_TYPE)
						.map((f) => ({
							type: 'MSG_FILE',
							id: f.id,
						})),
				];
			},
			infiniteQueryOptions: {
				initialPageParam: null,
				// limit: 1,
				getNextPageParam: (lastPage, allPages, lastPageParam, allPageParams) => {
					// console.log('GET NEXT PAGE', {
					// 	lastPage,
					// 	allPages,
					// 	lastPageParam,
					// 	allPageParams,
					// 	nextUrl: lastPage?.nextUrl,
					// });

					// const nextPage = lastPageParam.page + 1;
					// const remainingPages = lastPage?.totalPages - nextPage;

					// if (remainingPages <= 0) {
					// 	return undefined;
					// }

					// return {
					// 	...lastPageParam,
					// 	page: nextPage,
					// };
					// const nextPage = lastPageParam.page + 1;
					if (!lastPage?.nextUrl) {
						return undefined;
					}

					return lastPage?.nextUrl;
				},
				getPreviousPageParam: (firstPage, allPages, firstPageParam, allPageParams) => {
					// console.log('GET PREVIOUS PAGE', {
					// 	firstPage,
					// 	allPages,
					// 	firstPageParam,
					// 	allPageParams,
					// });

					// const prevPage = (allPages ?? [])[(allPages ?? []).length - 1];

					// const prevPage = firstPageParam.page - 1;
					// if (prevPage < 0) return undefined;

					// return {
					//   ...firstPageParam,
					//   page: prevPage,
					// }
					// return {
					// 	...firstPageParam,
					// 	page: prevPage,
					// 	currentUrl: firstPage?.prevUrl,
					// };
					return firstPageParam;
				},
			},
			queryFn: async (
				{ pageParam, queryArg },
				{ getState, dispatch },
				_extraOptions,
				baseQuery,
			) => {
				console.log('infiniteQuery QUERY ARGS', queryArg, pageParam);
				const { driveId, itemId, orderBy, direction, searchText, exact } = queryArg;
				const limit = 50;
				const url = pageParam;

				//can't pass to sharepoint it's our own custom property
				const isSentDateOrder = orderBy === 'sentDate';

				const order = isSentDateOrder
					? ''
					: `$orderby=${orderBy}${direction === 'asc' ? '' : '%20desc'}&`;

				const urlStart = `/drives/${driveId}/${
					itemId === 'root' ? itemId : `items/${itemId}`
				}/`;

				let nextUrl;
				const initialUrl = urlStart + 'children';
				const searchUrl = `${urlStart}${
					exact
						? `children?$filter=name eq '${escapeODataString(searchText)}'&`
						: `search(q='${escapeODataString(searchText)}')?`
				}${order}$top=${limit}`;

				try {
					const getFolders = async () => {
						let query = graphClient.api(initialUrl).top(limit);

						if (!isSentDateOrder) {
							query = query.orderby(
								`${orderBy}${direction === 'asc' ? '' : ' DESC'}`,
							);
						}
						query = query.expand('listItem');

						if ((searchText ?? '').length > 0) {
							query = graphClient.api(searchUrl);
						}

						const response =
							(url ?? '').length === 0
								? await query.get()
								: await graphClient.api(url).get();
						nextUrl = response?.['@odata.nextLink'];
						const clientDrives = response?.value ?? [];

						const { files: tempFiles, folders: tempFolders } = clientDrives.reduce(
							(acc, drive) => {
								const isFile = Object.keys(drive).includes('file');
								let props = {};
								if (drive.listItem) {
									props = { ...parseListItemMetadata(drive.listItem) };
								}
								if (isFile) {
									if (
										['application/vnd.ms-outlook', 'message/rfc822'].includes(
											drive?.file?.mimeType,
										)
									) {
										props.metadataLoading = true;
									}
									acc.files.push({ ...drive, ...props });
								} else {
									acc.folders.push({ ...drive, ...props });
								}

								return acc;
							},
							{ files: [], folders: [] },
						);

						if (isSentDateOrder) {
							tempFiles.sort((a, b) =>
								direction === 'asc'
									? Date.parse(a.lastModifiedDateTime) -
										Date.parse(b.lastModifiedDateTime)
									: Date.parse(b.lastModifiedDateTime) -
										Date.parse(a.lastModifiedDateTime),
							);
						}
						return { files: tempFiles, folders: tempFolders };
					};

					const { files, folders } = await getFolders();

					return {
						data: { folders, files, nextUrl, prevUrl: url },
					};
				} catch (error) {
					console.error('❌ [getItems query] Error retrieving client folders', error);
					return getError(error, dispatch);
				}
			},
			onQueryStarted: async (
				queryArg,
				{ dispatch, getState, queryFulfilled, updateCachedData },
			) => {
				let res;
				try {
					res = await queryFulfilled;
				} catch {
					return;
				}

				const { pages } = res.data;
				const { driveId, itemId, orderBy, direction, searchText, exact } = queryArg;
				const isSentDateOrder = orderBy === 'sentDate';

				for (let index = 0; index < pages.length; index++) {
					const { files, folders } = pages[index];
					const allItems = [...files, ...folders].slice().map((d) => ({ ...d }));

					const noListItem = allItems.filter((d) => !d.listItem);
					const batchedListItems = await dispatch(
						msGraphApi.endpoints.batchGetListItems.initiate({
							driveId,
							items: noListItem,
						}),
					).unwrap();

					for (const drive of allItems) {
						const needToPatch = !drive.listItem || drive.metadataLoading;

						if (!needToPatch) continue;

						if (batchedListItems[drive.id]) {
							drive.listItem = batchedListItems[drive.id];
						}
						const metadata = await getDriveMetadata({ drive, driveId, dispatch });

						updateCachedData((draft) => {
							const draftFolder = Object.keys(drive).includes('file')
								? 'files'
								: 'folders';
							if (needToPatch) {
								const fileIndex = draft.pages[index]?.[draftFolder].findIndex(
									(f) => f.id === drive.id,
								);
								if (fileIndex === -1) return;

								draft.pages[index][draftFolder][fileIndex] = {
									...draft.pages[index][draftFolder][fileIndex],
									...metadata,
									metadataLoading: false,
								};
							}
						});
					}

					updateCachedData((draft) => {
						if (isSentDateOrder) {
							draft.pages[index].files.sort((a, b) => {
								const aDate = a.sentDate
									? safelyParseDate(a.sentDate).getTime()
									: 0;
								const bDate = b.sentDate
									? safelyParseDate(b.sentDate).getTime()
									: 0;
								return direction === 'asc' ? aDate - bDate : bDate - aDate;
							});
						}
					});
				}
			},
		}),
		getFoldersAndFiles: builder.query({
			providesTags: (result, error, { driveId, itemId, recursive }) => {
				return [
					{ type: 'LIST_ITEM', id: `${driveId}-${itemId}` },
					{ type: 'ITEM', id: itemId },
					{ type: 'ITEMS', id: `${driveId}-${itemId}${recursive ? '-recursive' : ''}` },
					{ type: 'ITEMS', id: `${driveId}-${itemId}` },
					...(result?.files ?? [])
						.filter((f) => f?.file?.mimeType == MSG_TYPE)
						.map((f) => ({
							type: 'MSG_FILE',
							id: f.id,
						})),
				];
			},
			queryFn: async (
				{ driveId, itemId, recursive = false },
				{ getState, dispatch },
				_extraOptions,
				baseQuery,
			) => {
				try {
					const folders = [];
					const files = [];

					const getFolders = async (driveId, itemId) => {
						let nextLink = `/drives/${driveId}/items/${itemId}/children`;
						let clientDrives = [];
						while (nextLink) {
							const response = await graphClient
								.api(nextLink)
								// .top(999)
								.expand('listItem')
								.get();

							// console.log(`[getFoldersAndFiles] query response ${nextLink}`, response);
							nextLink = response?.['@odata.nextLink'];
							clientDrives = [...clientDrives, ...(response?.value ?? [])];
						}

						// Categorize into folders or files
						for (let drive of clientDrives) {
							const metadata = parseListItemMetadata(drive.listItem);
							drive = { ...drive, ...metadata };

							Object.keys(drive).includes('file')
								? files.push(drive)
								: folders.push(drive);
							// Recursively fetch subfolders if needed
							if ((drive.folder?.childCount ?? 0) > 0 && recursive) {
								await getFolders(driveId, drive.id);
							}
						}
					};

					await getFolders(driveId, itemId);

					console.log('FOLDERS AND FILES', folders, files);
					return { data: { folders, files } };
				} catch (error) {
					console.error(
						'❌ [getFoldersAndFiles query] Error retrieving client folders',
						error,
					);

					if ((error?.message ?? '').includes('not a valid folder')) {
						console.log('Not a valid folder');
						return {
							error: {
								code: 500,
								message: 'Not a valid folder',
							},
						};
					}
					return getError(error, dispatch);
				}
			},
		}),
		searchFoldersAndFiles: builder.query({
			providesTags: (result, error, args) => {
				return [
					// { type: 'ITEMS', id: `${args.driveId}-${args.itemId}-${args.searchText}` },
					...(result?.folders ?? []).concat(result?.files ?? []).map((f) => ({
						type: 'ITEM',
						id: f.id,
					})),
				];
			},
			queryFn: async (
				{ driveId, itemId, searchText = '', exact = false },
				{ getState, dispatch },
				_extraOptions,
				baseQuery,
			) => {
				try {
					console.log('search text', searchText);
					// const folders = [];
					// const files = [];
					// $filter=eq(name, '${searchText}')
					// $filter=name eq '${encodedFolderName}'
					const url = exact
						? `/drives/${driveId}/${
								itemId === 'root' ? itemId : `items/${itemId}`
							}/children?$filter=name eq '${escapeODataString(searchText)}'`
						: `/drives/${driveId}/${
								itemId === 'root' ? itemId : `items/${itemId}`
							}/search(q='${escapeODataString(searchText)}')`;
					const response = await graphClient
						.api(url)
						.get()
						.catch((e) => {
							throw new Error(e);
						});

					console.log(
						'✅ ~ [searchFoldersAndFiles] query',
						response, //.data,
						{
							driveId,
							itemId,
							searchText,
						},
					);
					const clientDrives = response?.value;
					console.log('SEARCHED CLIENT DRIVES', clientDrives);

					const { files, folders } = clientDrives.reduce(
						(acc, drive) => {
							const isFile = Object.keys(drive).includes('file');
							const props = parseListItemMetadata(drive.listItem);

							if (isFile) {
								if (
									['application/vnd.ms-outlook', 'message/rfc822'].includes(
										drive?.file?.mimeType,
									)
								) {
									props.metadataLoading = true;
								}
								acc.files.push({ ...drive, ...props });
							} else {
								acc.folders.push({ ...drive, ...props });
							}
							return acc;
						},
						{ files: [], folders: [] },
					);

					return { data: { folders, files } };
				} catch (error) {
					console.error(
						'❌ [searchFoldersAndFiles query] Error retrieving client folders',
						error,
					);
					return getError(error, dispatch);
				}
			},
			onQueryStarted: async (
				{ driveId, itemId, searchText = '', exact = false },
				{ dispatch, getState, queryFulfilled, updateCachedData },
			) => {
				let res;
				try {
					res = await queryFulfilled;
				} catch {
					return;
				}

				const { folders, files } = res.data;
				const allItems = [...files, ...folders].slice().map((d) => ({ ...d }));

				const noListItem = allItems.filter((d) => !d.listItem);
				const batchedListItems = await dispatch(
					msGraphApi.endpoints.batchGetListItems.initiate({ driveId, items: noListItem }),
				).unwrap();

				for (const drive of allItems) {
					console.log('DRIVE', drive);
					if (drive.listItem && !drive.metadataLoading) continue;
					if (batchedListItems[drive.id]) {
						drive.listItem = batchedListItems[drive.id];
					}
					const metadata = await getDriveMetadata({ drive, driveId, dispatch });

					console.log('Metadata', metadata);
					updateCachedData((draft) => {
						const draftFolder = Object.keys(drive).includes('file')
							? 'files'
							: 'folders';

						const fileIndex = draft[draftFolder].findIndex((f) => f.id === drive.id);
						if (fileIndex === -1) return;

						draft[draftFolder][fileIndex] = {
							...draft[draftFolder][fileIndex],
							...metadata,
							metadataLoading: false,
						};
					});
				}
			},
		}),
		getBreadcrumbs: builder.query({
			providesTags: (result, error, { driveId, startItemId, endItemId }) => {
				return [
					{
						type: 'BREADCRUMBS',
						id: `${driveId}-${startItemId}-${endItemId}`,
					},
				];
			},
			queryFn: async (
				{ driveId, startItemId, endItemId },
				{ getState, dispatch },
				_extraOptions,
				baseQuery,
			) => {
				try {
					const breadcrumbs = [];
					const getFolders = async (itemId) => {
						const driveItem = await dispatch(
							msGraphApi.endpoints.getDriveItem.initiate({
								driveId,
								itemId,
							}),
						).unwrap();

						if (driveItem) {
							breadcrumbs.push(driveItem);
						}

						if (itemId != startItemId && driveItem?.parentReference?.id) {
							await getFolders(driveItem.parentReference.id);
						}
					};
					await getFolders(endItemId);

					breadcrumbs.reverse();

					return { data: breadcrumbs };
				} catch (error) {
					console.error('❌ Error retrieving breadcrumbs', error);

					return getError(error, dispatch);
				}
			},
		}),
		searchSharepointSites: builder.query({
			queryFn: async ({ searchText }, { getState, dispatch }, _extraOptions, baseQuery) => {
				try {
					const response = await graphClient
						.api('/sites')
						.search(`"${escapeODataSearchPhrase(searchText)}"`)
						.get();
					console.log('✅ ~ [searchSharepointSites] retrieved successfully', response);

					return { data: response?.value?.[0]?.id };
				} catch (error) {
					console.error('❌ Error searching sharepoint sites:', error);
					return getError(error, dispatch);
				}
			},
		}),
		searchFilesByMetadata: builder.query({
			providesTags: (result, error, { driveId, itemId, metadata }) => {
				return [
					{
						type: 'ITEMS',
						id: `${driveId}-${itemId}-${metadata?.column}-${metadata?.value}`,
					},
					...(result ?? []).map((f) => ({
						type: 'ITEM',
						id: f.id,
					})),
				];
			},
			queryFn: async (
				{ driveId, itemId, metadata = { column: '', value: '' } },
				{ getState, dispatch },
				_extraOptions,
				baseQuery,
			) => {
				const { column, value } = metadata;
				try {
					const listId = await dispatch(
						msGraphApi.endpoints.getListIdFromDrive.initiate({
							driveId,
						}),
					).unwrap();
					const item = await dispatch(
						msGraphApi.endpoints.getListItem.initiate({
							driveId,
							itemId,
						}),
					).unwrap();

					const response = await graphClient
						.api(`/sites/${item.parentReference.siteId}/lists/${listId}/items`)
						.filter(`fields/${column} eq '${escapeODataString(value)}'`)
						.get();

					console.log(`✅ Files found with metadata ${column} ${value}:`, response); //.data);

					return { data: response?.value }; //data?.value };
				} catch (error) {
					console.error('❌ Error searching files by metadata:', error);
					return getError(error, dispatch);
				}
			},
		}),
		getDriveItem: builder.query({
			providesTags: (result, error, { driveId, itemId }) => {
				return [{ type: 'ITEM', id: itemId }];
			},
			queryFn: async (
				{ driveId, itemId },
				{ getState, dispatch },
				_extraOptions,
				baseQuery,
			) => {
				try {
					const response = await graphClient
						.api(`/drives/${driveId}/items/${itemId}`)
						.expand('listItem')
						.get();
					return { data: response }; //.data };
				} catch (error) {
					console.error('❌ Error retrieving drive item', error);
					return getError(error, dispatch);
				}
			},
		}),
		getDriveItemFromUrl: builder.query({
			queryFn: async (url, { getState, dispatch }, _extraOptions, baseQuery) => {
				try {
					const encodedUrl = encodeSharingUrl(url);

					const response = await graphClient
						.api(`/shares/${encodedUrl}/driveItem`)
						.expand('listItem')
						.get();

					console.log('✅ ~ [getDriveItemFromUrl] response', response);
					// console.log('✅ ~ Drive item:', response.data);

					return { data: response }; //.data };
				} catch (error) {
					console.error(`❌ Error retrieving drive item from url ${url}`, error);
					return getError(error, dispatch);
				}
			},
		}),
		getListItem: builder.query({
			providesTags: (result, error, { driveId, itemId }) => {
				return [{ type: 'LIST_ITEM', id: `${driveId}-${itemId}` }];
			},
			queryFn: async (
				{ driveId, itemId },
				{ getState, dispatch },
				_extraOptions,
				baseQuery,
			) => {
				try {
					const response = await graphClient
						.api(`/drives/${driveId}/items/${itemId}/listItem`)
						.get();

					return { data: response }; //.data };
				} catch (error) {
					console.error('❌ Error retrieving list item', error);
					return getError(error, dispatch);
				}
			},
		}),
		getSharepointLists: builder.query({
			providesTags: (result, error, { siteId }) => {
				return [{ type: 'LISTS', id: siteId }];
			},
			queryFn: async ({ siteId }, { getState, dispatch }, _extraOptions, baseQuery) => {
				try {
					const response = await graphClient.api(`/sites/${siteId}/lists`).get();

					console.log('✅ ~ SharePoint lists:', response?.value);

					return { data: response?.value };
				} catch (error) {
					console.error('❌ Error retrieving SharePoint list items', error);
					return getError(error, dispatch);
				}
			},
		}),
		getSharepointDrives: builder.query({
			providesTags: (result, error, { siteId }) => {
				return [{ type: 'DRIVES', id: siteId }];
			},
			queryFn: async ({ siteId }, { getState, dispatch }, _extraOptions, baseQuery) => {
				try {
					const response = await graphClient.api(`/sites/${siteId}/drives`).get();

					console.log('✅ ~ SharePoint drives:', response?.value);

					return { data: response?.value };
				} catch (error) {
					console.error('❌ Error retrieving SharePoint drives', error);
					return getError(error, dispatch);
				}
			},
		}),
		getListIdFromDrive: builder.query({
			providesTags: (result, error, { driveId }) => {
				return [{ type: 'LIST', id: driveId }];
			},
			queryFn: async ({ driveId }, { getState, dispatch }, _extraOptions, baseQuery) => {
				try {
					const response = await graphClient.api(`/drives/${driveId}/list`).get();

					console.log('✅ ~ List information', response); //.data);

					return { data: response?.id }; //.data.id };
				} catch (error) {
					console.error('❌ Error retrieving list information', error);
					return getError(error, dispatch);
				}
			},
		}),
		getEventData: builder.query({
			providesTags: (result, error, { taskType, eventId }) => {
				return [{ type: 'TASK', id: `${taskType}-${eventId}` }];
			},
			queryFn: async (
				{ taskType, eventId },
				{ getState, dispatch },
				_extraOptions,
				baseQuery,
			) => {
				try {
					const endpoint = `${
						taskType === 'MEETING' ? '/me/calendar' : '/me'
					}/events/${eventId}`;
					const res = await graphClient.api(endpoint).get();
					console.log('[getEventData] res', res);
					return { data: res };
				} catch (error) {
					console.error('❌ Error getting event data:', error, { taskType, eventId });
					return getError(error, dispatch);
				}
			},
		}),
		getUserProfile: builder.query({
			queryFn: async (user, { getState, dispatch }, _extraOptions, baseQuery) => {
				try {
					const res = await graphClient.api('/me').get();
					console.log('[getUserProfile] res', res);

					return { data: res };
				} catch (error) {
					console.error('❌ Error getting user profile:', error);
					return getError(error, dispatch);
				}
			},
		}),
		getUserByEmail: builder.query({
			queryFn: async ({ email }, { getState, dispatch }, _extraOptions, baseQuery) => {
				try {
					const res = await graphClient.api(`/users/${email}`).get();
					console.log('[getUserByEmail] res', res);

					return { data: res };
				} catch (error) {
					console.error(`❌ Error getting user data by email ${email}:`, error);
					return getError(error, dispatch);
				}
			},
		}),
		getUserCalendars: builder.query({
			queryFn: async ({ email }, { getState, dispatch }, _extraOptions, baseQuery) => {
				try {
					const userId = await dispatch(
						msGraphApi.endpoints.getUserByEmail.initiate({
							email,
						}),
					).unwrap();
					const res = await graphClient.api(`/users/${userId}/calendars`).get();
					console.log('✅ ~ Calendars retrieved successfully:', res);
					return { data: res };
				} catch (error) {
					console.error('❌ Error getting user calendars:', error, { email });
					return getError(error, dispatch);
				}
			},
		}),
		getInbox: builder.query({
			queryFn: async ({ page = 0 }, { getState, dispatch }, _extraOptions, baseQuery) => {
				try {
					const res = await graphClient
						.api('/me/mailFolders/inbox/messages')
						.top(10)
						.skip(page * 10)
						.select('subject,body,from,receivedDateTime,toRecipients')
						.orderby('receivedDateTime DESC')
						.get();
					console.log('✅ ~ Inbox retrieved successfully:', res);
					return { data: res.value };
				} catch (error) {
					console.error('❌ Error getting inbox:', error, { page });
					return getError(error, dispatch);
				}
			},
		}),
		getSentItems: builder.query({
			queryFn: async ({ page = 0 }, { getState, dispatch }, _extraOptions, baseQuery) => {
				try {
					const res = await graphClient
						.api('/me/mailFolders/sentitems/messages')
						.top(10)
						.skip(page * 10)
						.select('subject,body,from,receivedDateTime,toRecipients')
						.orderby('receivedDateTime DESC')
						.get();
					console.log('✅ ~ Sent items retrieved successfully:', res);
					return { data: res.value };
				} catch (error) {
					console.error('❌ Error getting sent items:', error, { page });
					return getError(error, dispatch);
				}
			},
		}),
		// getSentEmail: builder.query({
		// 	// invalidatesTags: ['TASK'],
		// 	queryFn: async ({ messageId }, { getState, dispatch }, _extraOptions, baseQuery) => {
		// 		try {
		// 			const sentMessage = await graphClient.api(`/me/messages/${messageId}`).get();

		// 			const mimeResponse = await graphClient
		// 				.api(`/me/messages/${messageId}/$value`)
		// 				.responseType('blob')
		// 				.get();

		// 			return { data: { message: sentMessage, content: mimeResponse } };
		// 		} catch (error) {
		// 			console.error('❌ Error sending email:', error);

		// 			const { code, message } = getError(error, dispatch);
		// 			return {
		// 				error: { code, message },
		// 			};
		// 		}
		// 	},
		// }),
		// /// MUTATIONS
		addFileToSharepointBySession: builder.mutation({
			invalidatesTags: (result, error, { driveId, itemId }) => [
				{ type: 'LIST_ITEM', id: `${driveId}-${itemId}` },
				{ type: 'ITEM', id: itemId },
				{
					type: 'ITEMS',
					id: `${driveId}-${itemId}-recursive}`,
				},
				{
					type: 'ITEMS',
					id: `${driveId}-${itemId}}`,
				},
			],
			queryFn: async (
				{ driveId, itemId, file, metadata = {} },
				{ getState, dispatch },
				_extraOptions,
				baseQuery,
			) => {
				try {
					const { fileContent, fileSize } = await prepareFileForUpload({
						file,
						metadata,
					});

					const sessionResponse = await graphClient
						.api(
							`/drives/${driveId}/items/${itemId}:/${file.name}:/createUploadSession`,
						)
						.post({
							item: {
								'@microsoft.graph.conflictBehavior': 'rename', // Ensure no file overwrites
								name: file.name,
							},
						});

					const uploadUrl = sessionResponse.uploadUrl;
					console.log('✅ ~ Upload session created:', uploadUrl);

					const fileRes = await uploadFileContentToSession({
						uploadUrl,
						fileContent,
						fileSize,
					});

					console.log('✅ ~ File uploaded successfully!', fileRes);
					if (Object.keys(metadata).length > 0 && fileRes?.id) {
						await addMetadataToUploadedFile({
							dispatch,
							driveId,
							itemId: fileRes.id,
							metadata,
						});
					}
					return { data: fileRes };
				} catch (error) {
					console.error('❌ Error uploading file via session:', error);
					return getError(error, dispatch);
				}
			},
		}),
		batchAddFilesToSharepoint: builder.mutation({
			invalidatesTags: (result, error, { driveId, itemId }) => [
				{ type: 'LIST_ITEM', id: `${driveId}-${itemId}` },
				{ type: 'ITEM', id: itemId },
				{
					type: 'ITEMS',
					id: `${driveId}-${itemId}-recursive}`,
				},
				{
					type: 'ITEMS',
					id: `${driveId}-${itemId}}`,
				},
			],
			queryFn: async (
				{ driveId, itemId, files = [] },
				{ getState, dispatch },
				_extraOptions,
				baseQuery,
			) => {
				try {
					if (!driveId || !itemId || files.length === 0) {
						throw new Error('driveId, itemId, and files are required');
					}

					const uploadedFiles = [];
					const failedFiles = [];
					const preparedFiles = await Promise.all(files.map(prepareFileForUpload));

					for (const fileChunk of chunk(preparedFiles, 20)) {
						const requests = buildUploadSessionBatchRequests({
							driveId,
							itemId,
							files: fileChunk,
						});
						const batchResponse = await graphClient.api('/$batch').post({ requests });
						const responses = batchResponse?.responses ?? [];

						await Promise.all(
							fileChunk.map(async (entry, index) => {
								const requestId = `req_${index + 1}`;
								const result = responses.find(
									(response) => response.id === requestId,
								);

								if (
									!(result?.status >= 200 && result?.status < 300) ||
									!result?.body?.uploadUrl
								) {
									failedFiles.push({
										name: entry.file.name,
										status: result?.status ?? 500,
										error:
											result?.body?.error ??
											result?.body ??
											'Failed to create upload session',
									});
									return;
								}

								try {
									const fileRes = await uploadFileContentToSession({
										uploadUrl: result.body.uploadUrl,
										fileContent: entry.fileContent,
										fileSize: entry.fileSize,
									});

									if (Object.keys(entry.metadata).length > 0 && fileRes?.id) {
										await addMetadataToUploadedFile({
											dispatch,
											driveId,
											itemId: fileRes.id,
											metadata: entry.metadata,
										});
									}

									uploadedFiles.push(fileRes);
								} catch (error) {
									const { error: graphError } = getError(error, dispatch);
									failedFiles.push({
										name: entry.file.name,
										status: graphError.statusCode ?? error?.statusCode ?? 500,
										error: graphError,
									});
								}
							}),
						);
					}

					await invalidateFilesTags(dispatch, { driveId, itemId });

					let cachePatched = false;
					try {
						cachePatched = patchGetItemsCachesAfterCreate({
							dispatch,
							getState,
							driveId,
							parentItemId: itemId,
							createdItems: uploadedFiles,
						});
					} catch (e) {
						console.log('Error patching getItems after batch upload', e);
					}

					return {
						data: {
							uploadedFiles,
							failedFiles,
							cachePatched,
						},
					};
				} catch (error) {
					console.error('❌ Error batch uploading files:', error);
					return getError(error, dispatch);
				}
			},
		}),
		addFileToSharepoint: builder.mutation({
			invalidatesTags: (result, error, { driveId, itemId }) => [
				{ type: 'LIST_ITEM', id: `${driveId}-${itemId}` },
				{ type: 'ITEM', id: itemId },
				{
					type: 'ITEMS',
					id: `${driveId}-${itemId}-recursive}`,
				},
				{
					type: 'ITEMS',
					id: `${driveId}-${itemId}}`,
				},
			],
			queryFn: async (
				{ driveId, itemId, file, metadata = {} },
				{ getState, dispatch },
				_extraOptions,
				baseQuery,
			) => {
				try {
					const data = await dispatch(
						msGraphApi.endpoints.addFileToSharepointBySession.initiate({
							driveId,
							itemId,
							file,
							metadata,
						}),
					).unwrap();

					await invalidateFilesTags(dispatch, { driveId, itemId });

					let cachePatched = false;
					try {
						cachePatched = patchGetItemsCachesAfterCreate({
							dispatch,
							getState,
							driveId,
							parentItemId: itemId,
							createdItems: [data],
						});
					} catch (e) {
						console.log('Error patching getItems after file upload', e);
					}

					return { data: { ...data, cachePatched } };
				} catch (error) {
					console.error('❌ Error uploading file:', error);
					const errorMessage =
						error?.response?.data?.error?.message ??
						error?.message ??
						'Error uploading file; please try again.';
					const pathIsTooLong =
						error?.response?.data?.error?.innerError?.code == 'pathIsTooLong';
					const additionalMessage = pathIsTooLong
						? ' Please try shortening folder or file names, or reduce number of sub-folders.'
						: '';
					const { error: graphError } = getError(error, dispatch);
					return {
						error: { ...graphError, message: `${errorMessage}${additionalMessage}` },
					};
				}
			},
		}),
		addFolderToSharepoint: builder.mutation({
			invalidatesTags: (result, error, { driveId, itemId }) => [
				{ type: 'LIST_ITEM', id: `${driveId}-${itemId}` },
				{ type: 'ITEM', id: itemId },
				{
					type: 'ITEMS',
					id: `${driveId}-${itemId}-recursive}`,
				},
				// {
				// 	type: 'ITEMS',
				// 	id: `${driveId}-${itemId}}`,
				// },
			],
			queryFn: async (
				{ driveId, itemId, folderName },
				{ getState, dispatch },
				_extraOptions,
				baseQuery,
			) => {
				try {
					let formattedFolderName;
					try {
						formattedFolderName = decodeURI(folderName.normalize());
					} catch (e) {
						console.log('Error decoding uri', e);
						formattedFolderName = folderName.normalize();
					}

					const body = {
						name: formattedFolderName,
						folder: {},
					};

					const response = await graphClient
						.api(`/drives/${driveId}/items/${itemId}/children`)
						.post(body);
					console.log('Created folder', response);

					try {
						const patchResult = dispatch(
							msGraphApi.util.updateQueryData(
								'getFoldersAndFiles',
								{
									itemId,
									driveId,
									recursive: false,
								},
								(draft) => {
									if (draft?.folders) {
										const folders = [...draft.folders, response];
										Object.assign(draft, {
											...draft,
											folders,
										});
									}
								},
							),
						);
					} catch (e) {
						console.log('Error patching files', e);
					}

					let cachePatched = false;
					try {
						cachePatched = patchGetItemsCachesAfterCreate({
							dispatch,
							getState,
							driveId,
							parentItemId: itemId,
							createdItems: [response],
						});
					} catch (e) {
						console.log('Error patching getItems after folder create', e);
					}

					return { data: { ...response, cachePatched } };
				} catch (error) {
					console.error('❌ Error creating folder:', error);
					return getError(error, dispatch);
				}
			},
		}),
		batchGetListItems: builder.mutation({
			queryFn: async ({ driveId, items = [] }, { dispatch }) => {
				const listItemById = {};
				if (!items.length) return { data: listItemById };

				for (const itemChunk of chunk(items, 20)) {
					const requests = buildListItemBatchRequests({ driveId, items: itemChunk });
					const { responses = [] } = await graphClient.api('/$batch').post({ requests });

					for (const response of responses) {
						const index = Number(response.id.replace('req_', '')) - 1;
						const item = itemChunk[index];
						if (!item) continue;

						if (response.status >= 200 && response.status < 300) {
							listItemById[item.id] = response.body;

							dispatch(
								msGraphApi.util.upsertQueryData(
									'getListItem',
									{ driveId, itemId: item.id },
									response.body,
								),
							);
						}
					}
				}

				return { data: listItemById };
			},
		}),
		batchAddFoldersToSharepoint: builder.mutation({
			invalidatesTags: (result, error, { driveId, itemId }) => [
				{ type: 'LIST_ITEM', id: `${driveId}-${itemId}` },
				{ type: 'ITEM', id: itemId },
				{
					type: 'ITEMS',
					id: `${driveId}-${itemId}-recursive}`,
				},
				{
					type: 'ITEMS',
					id: `${driveId}-${itemId}}`,
				},
			],
			queryFn: async (
				{ driveId, itemId, folders = [] },
				{ getState, dispatch },
				_extraOptions,
				baseQuery,
			) => {
				try {
					if (!driveId || !itemId || folders.length === 0) {
						throw new Error('driveId and rootItemId and folders are required');
					}
					console.log('[batchAddFoldersToSharepoint] folders', folders);
					const createdFolders = {};
					const failedFolders = [];
					const itemIdByPath = new Map();
					const permanentlyFailed = new Set();

					const abandonSubtree = (folder, error, remainingList) => {
						const prefix = `${folder.path}/`;
						remainingList
							.filter((f) => f.path === folder.path || f.path.startsWith(prefix))
							.forEach((f) => {
								failedFolders.push({
									path: f.path,
									name: f.name,
									parentPath: f.parentPath,
									error,
								});
								permanentlyFailed.add(f.path);
							});
					};

					let remaining = folders.slice();

					while (remaining.length > 0) {
						const readyFolders = remaining.filter(
							(folder) => !folder.parentPath || itemIdByPath.has(folder.parentPath),
						);

						if (readyFolders.length === 0) {
							remaining.forEach((f) =>
								failedFolders.push({
									path: f.path,
									name: f.name,
									parentPath: f.parentPath,
									error: { message: 'Parent folder was never created' },
								}),
							);
							break;
						}

						for (const createChunk of chunk(readyFolders, 20)) {
							const requests = buildBatchRequests({
								driveId,
								chunk: createChunk,
								itemIdByPath,
								itemId,
							});
							const batchResponse = await graphClient
								.api('/$batch')
								.post({ requests })
								.catch((e) => {
									console.log('[batchAddFoldersToSharepoint] Error in create batch', e);
									return { responses: [] };
								});
							console.log('[batchAddFoldersToSharepoint] response', batchResponse);
							const responses = batchResponse?.responses ?? [];

							createChunk.forEach((folder, index) => {
								const result = responses.find((r) => r.id === `req_${index + 1}`);

								if (
									result?.status >= 200 &&
									result?.status < 300 &&
									result?.body?.id
								) {
									itemIdByPath.set(folder.path, result.body.id);
									createdFolders[folder.path] = result.body;
								} else {
									abandonSubtree(
										folder,
										result?.body?.error ??
											result ?? { message: 'Unknown batch error' },
										remaining,
									);
								}
							});
						}
						remaining = remaining.filter(
							(folder) =>
								!itemIdByPath.has(folder.path) && !permanentlyFailed.has(folder.path),
						);
					}

					if (failedFolders.length > 0) {
						console.warn(
							'[batchAddFoldersToSharepoint] some folders were not created',
							failedFolders,
						);
					}

					return {
						data: {
							createdFolders,
							failedFolders,
						},
					};
				} catch (error) {
					console.error('❌ Error creating folder:', error);
					return getError(error, dispatch);
				}
			},
		}),
		renameSharepointItem: builder.mutation({
			// invalidatesTags: (result, error, { driveId, itemId }) => [
			// 	{ type: 'LIST_ITEM', id: `${driveId}-${itemId}` },
			// 	{ type: 'ITEM', id: itemId },
			// 	{
			// 		type: 'ITEMS',
			// 		id: `${driveId}-${itemId}-recursive}`,
			// 	},
			// 	{
			// 		type: 'ITEMS',
			// 		id: `${driveId}-${itemId}}`,
			// 	},
			// ],
			queryFn: async (
				{ driveId, itemId, name },
				{ getState, dispatch },
				_extraOptions,
				baseQuery,
			) => {
				try {
					const body = { name };

					const response = await graphClient
						.api(`/drives/${driveId}/items/${itemId}`)
						.patch(body);

					console.log(
						`✅ ~ Item ${itemId} renamed successfully to ${name}:`,
						response, //.data
					);

					try {
						dispatch(
							msGraphApi.util.updateQueryData(
								'getDriveItem',
								{
									itemId,
									driveId,
								},
								(draft) => {
									Object.assign(draft, {
										...draft,
										...response,
									});
								},
							),
						);
						dispatch(
							msGraphApi.util.updateQueryData(
								'getFoldersAndFiles',
								{
									itemId: response.parentReference.id,
									driveId,
									recursive: false,
								},
								(draft) => {
									if (draft?.folders && response.folder) {
										const folders = [
											...draft.folders.map((f) =>
												f.id === response.id
													? { ...f, ...response }
													: { ...f },
											),
										];
										Object.assign(draft, {
											...draft,
											folders,
										});
									} else if (draft?.files && !response.folder) {
										const files = [
											...draft.files.map((f) =>
												f.id === response.id
													? { ...f, ...response }
													: { ...f },
											),
										];
										Object.assign(draft, {
											...draft,
											files,
										});
									}
								},
							),
						);
						patchGetItemsCachesByItemId({
							dispatch,
							getState,
							itemId: response.id,
							updater: (existing) => ({
								...existing,
								...response,
							}),
						});
					} catch (e) {
						console.log('Error patching files', e);
					}
					return { data: response }; //.data };
				} catch (error) {
					console.error('❌ Error renaming item', error);
					return getError(error, dispatch);
				}
			},
		}),
		addMetadataToFile: builder.mutation({
			// invalidatesTags: (result, error, { driveId, itemId }) => [
			// 	{ type: 'LIST_ITEM', id: `${driveId}-${itemId}` },
			// 	{ type: 'ITEM', id: itemId },
			// 	{
			// 		type: 'ITEMS',
			// 		id: `${driveId}-${itemId}-recursive}`,
			// 	},
			// 	{
			// 		type: 'ITEMS',
			// 		id: `${driveId}-${itemId}}`,
			// 	},
			// ],
			queryFn: async (
				{ driveId, itemId, metadata },
				{ getState, dispatch },
				_extraOptions,
				baseQuery,
			) => {
				try {
					const listId = await dispatch(
						msGraphApi.endpoints.getListIdFromDrive.initiate({
							driveId,
						}),
					).unwrap();
					const item = await dispatch(
						msGraphApi.endpoints.getListItem.initiate({
							driveId,
							itemId,
						}),
					).unwrap();

					const response = await graphClient
						.api(
							`/sites/${item.parentReference.siteId}/lists/${listId}/items/${item.id}/fields`,
						)
						.patch(metadata);

					console.log('✅ ~ Metadata added successfully:', response);

					try {
						const patchListItemCache = (draft) => {
							draft.fields = { ...(draft.fields ?? {}), ...response };
						};

						const patchGetItemsRow = (existing) => {
							const baseListItem = existing.listItem ?? item;
							const updatedListItem = baseListItem
								? {
										...baseListItem,
										fields: {
											...(baseListItem.fields ?? {}),
											...response,
										},
									}
								: { fields: response };

							return {
								...existing,
								...parseListItemMetadata(updatedListItem),
								metadata: { ...(existing.metadata ?? {}), ...response },
								metadataLoading: false,
								listItem: updatedListItem,
							};
						};

						dispatch(
							msGraphApi.util.updateQueryData(
								'getListItem',
								{ itemId, driveId },
								patchListItemCache,
							),
						);

						patchGetItemsCachesByItemId({
							dispatch,
							getState,
							itemId,
							updater: patchGetItemsRow,
						});
					} catch (e) {
						console.log('Error patching files', e);
					}

					return { data: response };
				} catch (error) {
					console.error('❌ Error adding metadata to item', error);

					const status = error?.statusCode;
					const message = error?.message ?? 'Error adding metadata to item';
					return {
						error: { status, message },
					};
				}
			},
		}),
		moveSharepointItem: builder.mutation({
			invalidatesTags: (result, error, { driveId, itemId, newParentId }) => [
				{ type: 'LIST_ITEM', id: `${driveId}-${itemId}` },
				{ type: 'ITEM', id: itemId },
				{
					type: 'ITEMS',
					id: `${driveId}-${itemId}-recursive}`,
				},
				{
					type: 'ITEMS',
					id: `${driveId}-${itemId}}`,
				},
				{ type: 'LIST_ITEM', id: `${driveId}-${newParentId}` },
				{ type: 'ITEM', id: newParentId },
				{
					type: 'ITEMS',
					id: `${driveId}-${newParentId}-recursive}`,
				},
				{
					type: 'ITEMS',
					id: `${driveId}-${newParentId}}`,
				},
				'BREADCRUMBS',
			],
			queryFn: async (
				{ driveId, itemId, newParentId },
				{ getState, dispatch },
				_extraOptions,
				baseQuery,
			) => {
				try {
					const body = {
						parentReference: { id: newParentId },
						'@microsoft.graph.conflictBehavior': 'rename',
					};

					const item = await dispatch(
						msGraphApi.endpoints.getDriveItem.initiate({
							driveId,
							itemId,
						}),
					)
						.unwrap()
						.catch((e) =>
							console.log('Error getting existing item', e, { driveId, itemId }),
						);

					const response = await graphClient
						.api(`/drives/${driveId}/items/${itemId}`)
						.patch(body);
					// const response = await baseQuery({
					// 	url: `/drives/${driveId}/items/${itemId}`,
					// 	method: 'PATCH',
					// 	data: body,
					// });

					console.log(`✅ ~ Item ${itemId} moved successfully:`, response); //.data);

					try {
						const patchResultOldParent = dispatch(
							msGraphApi.util.updateQueryData(
								'getFoldersAndFiles',
								{
									itemId: item.parentReference.id,
									driveId,
									recursive: false,
								},
								(draft) => {
									if (draft?.folders && response.folder) {
										const folders = [
											...draft.folders.filter((i) => i.id !== item.id),
										];
										Object.assign(draft, {
											...draft,
											folders,
										});
									} else if (draft?.files && !response.folder) {
										const files = [
											...draft.files.filter((i) => i.id !== item.id),
										];
										Object.assign(draft, {
											...draft,
											files,
										});
									}
								},
							),
						);
						const patchResultItem = dispatch(
							msGraphApi.util.updateQueryData(
								'getDriveItem',
								{
									itemId,
									driveId,
								},
								(draft) => {
									Object.assign(draft, {
										...draft,
										...response,
									});
								},
							),
						);
						const patchResultNewParent = dispatch(
							msGraphApi.util.updateQueryData(
								'getFoldersAndFiles',
								{
									itemId: newParentId,
									driveId,
									recursive: false,
								},
								(draft) => {
									if (draft?.folders && response.folder) {
										const folders = [...draft.folders, response];
										Object.assign(draft, {
											...draft,
											folders,
										});
									} else if (draft?.files && !response.folder) {
										const files = [...draft.files, response];
										Object.assign(draft, {
											...draft,
											files,
										});
									}
								},
							),
						);
					} catch (e) {
						console.log('Error patching files', e);
					}
					await invalidateFilesTags(dispatch, { driveId, itemId });
					await invalidateFilesTags(dispatch, { driveId, itemId: newParentId });
					return { data: response.data };
				} catch (error) {
					console.error('❌ Error moving item', error);
					const errorMessage =
						error?.response?.data?.error?.message ??
						error?.message ??
						'Error moving items; please try again.';
					const pathIsTooLong =
						error?.response?.data?.error?.innerError?.code == 'pathIsTooLong';
					const additionalMessage = pathIsTooLong
						? ' Please try shortening folder or file names, or reduce number of sub-folders.'
						: '';

					const { error: graphError } = getError(error, dispatch);
					return {
						error: { ...graphError, message: `${errorMessage}${additionalMessage}` },
					};
				}
			},
		}),
		deleteSharepointItem: builder.mutation({
			invalidatesTags: (result, error, { driveId, itemId }) => [
				{ type: 'LIST_ITEM', id: `${driveId}-${itemId}` },
				{ type: 'ITEM', id: itemId },
				{
					type: 'ITEMS',
					id: `${driveId}-${itemId}-recursive}`,
				},
				{
					type: 'ITEMS',
					id: `${driveId}-${itemId}}`,
				},
			],
			queryFn: async (
				{ driveId, itemId },
				{ getState, dispatch },
				_extraOptions,
				baseQuery,
			) => {
				try {
					const item = await dispatch(
						msGraphApi.endpoints.getDriveItem.initiate({
							driveId,
							itemId,
						}),
					)
						.unwrap()
						.catch((e) =>
							console.log('Error getting existing item', e, { driveId, itemId }),
						);

					const response = await graphClient
						.api(`/drives/${driveId}/items/${itemId}`)
						.delete();

					console.log(`✅ ~ Item ${itemId} deleted successfully:`, response); //.data);

					const data = response; //.data;

					try {
						const patchResult = dispatch(
							msGraphApi.util.updateQueryData(
								'getFoldersAndFiles',
								{
									itemId: item.parentReference.id,
									driveId,
									recursive: false,
								},
								(draft) => {
									if (draft?.folders && item.folder) {
										const folders = [
											...draft.folders.filter((i) => i.id !== item.id),
										];
										Object.assign(draft, {
											...draft,
											folders,
										});
									} else if (draft?.files && !response.folder) {
										const files = [
											...draft.files.filter((i) => i.id !== item.id),
										];
										Object.assign(draft, {
											...draft,
											files,
										});
									}
								},
							),
						);
					} catch (e) {
						console.log('Error patching files', e);
					}
					await invalidateFilesTags(dispatch, { driveId, itemId });
					return { data };
				} catch (error) {
					console.error('❌ Error deleting item', error);
					return getError(error, dispatch);
				}
			},
		}),
		createEvent: builder.mutation({
			// invalidatesTags: ['TASK'],
			queryFn: async (
				{ taskType, eventDetails },
				{ getState, dispatch },
				_extraOptions,
				baseQuery,
			) => {
				try {
					const endpoint = `${taskType === 'MEETING' ? '/me/calendar' : '/me'}/events`;
					console.log('Event details for new event', { eventDetails });
					const res = await graphClient.api(endpoint).post(eventDetails);
					console.log('🚀 ~ Event created successfully', res);

					return { data: res }; //.data };
				} catch (error) {
					console.error('❌ Error creating task:', error);

					return getError(error, dispatch);
				}
			},
		}),
		updateEvent: builder.mutation({
			invalidatesTags: (result, error, { taskType, eventId }) => [
				{ type: 'LIST_ITEM', id: `${taskType}-${eventId}` },
			],
			queryFn: async (
				{ taskType, eventId, eventData },
				{ getState, dispatch },
				_extraOptions,
				baseQuery,
			) => {
				try {
					const endpoint = `${
						taskType === 'MEETING' ? '/me/calendar' : '/me'
					}/events/${eventId}`;

					const res = await graphClient.api(endpoint).patch(eventData);
					console.log('🚀 ~ Event updated successfully', res);

					return { data: res }; //.data };
				} catch (error) {
					console.error('❌ Error updating task:', error);

					return getError(error, dispatch);
				}
			},
		}),
		deleteEmail: builder.mutation({
			// invalidatesTags: ['TASK'],
			queryFn: async ({ emailId }, { getState, dispatch }, _extraOptions, baseQuery) => {
				try {
					const res = await graphClient.api(`/me/messages/${emailId}`).delete();
					console.log('🚀 ~ Email deleted successfully', res);

					return { data: res }; //.data };
				} catch (error) {
					console.error('❌ Error deleting email:', error);

					return getError(error, dispatch);
				}
			},
		}),
		sendEmail: builder.mutation({
			// invalidatesTags: ['TASK'],
			queryFn: async (
				{
					recipient = '',
					cc = '',
					bcc = '',
					subject,
					body,
					images = [],
					files = [],
					from,
					email,
				},
				{ getState, dispatch },
				_extraOptions,
				baseQuery,
			) => {
				try {
					console.log('[sendEmail] params', {
						recipient,
						cc,
						bcc,
						subject,
						body,
						images,
						files,
						from,
						email,
					});
					const fileAttachments = await Promise.all(
						files.map((file) => convertFileToGraphAttachment(file)),
					);

					const attachments = images
						.map((base64, index) => {
							const mimeTypeMatch = base64.match(/^data:(image\/\w+);base64,/);
							if (!mimeTypeMatch) return null;
							const mimeType = mimeTypeMatch[1];
							const base64Data = base64.replace(/^data:image\/\w+;base64,/, '');
							const type = mimeType.replace('image/', '.');
							return {
								'@odata.type': '#microsoft.graph.fileAttachment',
								name: `image${index + 1}${type}`,
								contentType: mimeType,
								contentBytes: base64Data,
								contentId: `image${index + 1}`,
								isInline: true,
							};
						})
						.concat(fileAttachments)
						.filter(Boolean);

					const graphAccessToken = getState()?.msalAccount?.accessToken;
					if (!graphAccessToken) {
						throw new Error('Missing Microsoft access token');
					}

					const response = await authBaseQuery(
						{
							url: process.env.REACT_APP_SEND_EMAIL,
							method: 'POST',
							body: {
								recipient,
								cc,
								bcc,
								subject,
								body,
								from,
								email,
								attachments,
							},
							headers: {
								'ms-token': graphAccessToken,
							},
						},
						{ getState, dispatch },
						_extraOptions,
					);

					if (response.error) {
						const errorMessage =
							response.error?.data?.message ??
							response.error?.message ??
							'Error sending email';
						return {
							error: {
								message: errorMessage,
								code: response.error?.status ?? 'GRAPH_ERROR',
							},
						};
					}

					return { data: response.data?.data ?? response.data };
				} catch (error) {
					const message =
						error?.data?.message ??
						error?.response?.error?.message ??
						error?.message ??
						'Error sending email';
					console.error(
						`❌ Error sending email via user email ${email} using email ${from ?? email}:`,
						error,
					);
					return {
						error: {
							message: typeof message === 'string' ? message : 'Error sending email',
							code: error?.status || error?.code || 'GRAPH_ERROR',
						},
					};
				}
			},
		}),
		copySharepointItem: builder.mutation({
			invalidatesTags: (result, error, { targetDriveId }) => [
				{ type: 'LIST_ITEM', id: `${targetDriveId}-root` },
				{ type: 'ITEM', id: 'root' },
				{ type: 'ITEMS', id: `${targetDriveId}-root-recursive}` },
				'BREADCRUMBS',
			],

			queryFn: async (
				{ sourceDriveId, itemId, targetDriveId, subFolderDepth = 4 },
				{ dispatch },
			) => {
				const copyFolderRecursive = async (
					sourceDriveId,
					sourceItemId,
					targetDriveId,
					targetParentId = null,
					results = [],
					depth = 1,
				) => {
					if (depth > subFolderDepth) return results;
					const sourceItem = await graphClient
						.api(`/drives/${sourceDriveId}/items/${sourceItemId}`)
						.get();
					if (!sourceItem.folder) return results;

					const itemName = sourceItem.name;
					const newFolder = await graphClient
						.api(`/drives/${targetDriveId}/items/${targetParentId || 'root'}/children`)
						.post({
							name: itemName,
							folder: {},
							'@microsoft.graph.conflictBehavior': 'replace',
						});

					results.push({
						id: newFolder.id,
						name: newFolder.name,
						type: 'folder',
						parentId: targetParentId || 'root',
						parentFolderName: sourceItem.parentReference?.name || null,
						depth,
					});
					if (depth < subFolderDepth) {
						const children = await graphClient
							.api(`/drives/${sourceDriveId}/items/${sourceItemId}/children`)
							.get();

						for (const child of children.value) {
							if (!child.folder) continue;
							await copyFolderRecursive(
								sourceDriveId,
								child.id,
								targetDriveId,
								newFolder.id,
								results,
								depth + 1,
							);
						}
					}

					return results;
				};

				try {
					const results = await copyFolderRecursive(sourceDriveId, itemId, targetDriveId);
					return {
						data: {
							status: 'Folder copy complete (folders only, 4 levels deep)',
							items: results,
						},
					};
				} catch (error) {
					console.error('❌ Error copying item', error);
					const errorPayload = {
						code: 'UNKNOWN_ERROR',
						message: 'Error copying item, please try again.',
					};
					try {
						const { error: extracted } = getError(error, dispatch);
						if (extracted?.code) errorPayload.code = extracted.code;
						if (extracted?.message) errorPayload.message = extracted.message;
					} catch (err) {
						console.log('Error extracting error details', err);
					}
					return { error: errorPayload };
				}
			},
		}),
		getUserEmails: builder.query({
			queryFn: async ({ email }, { getState, dispatch }, _extraOptions, baseQuery) => {
				try {
					const res = await graphClient.api('/me?$select=proxyAddresses').get();
					console.log('✅ successfully retrieved user data', res);
					return { data: res };
				} catch (error) {
					console.error('❌ Error getting users email aliases:', error, { email });
					return getError(error, dispatch);
				}
			},
		}),
	}),
});

export const {
	useGetItemsInfiniteQuery,
	useGetFoldersAndFilesQuery,
	useSearchFoldersAndFilesQuery,
	useLazyGetFoldersAndFilesQuery,
	useLazySearchFoldersAndFilesQuery,
	useSearchFilesByMetadataQuery,
	useLazySearchFilesByMetadataQuery,
	useGetSharepointDrivesQuery,
	useLazyGetSharepointDrivesQuery,
	useGetBreadcrumbsQuery,
	useSearchSharepointSitesQuery,
	useLazySearchSharepointSitesQuery,
	useGetListItemQuery,
	useLazyGetListItemQuery,
	useGetDriveItemQuery,
	useGetDriveItemFromUrlQuery,
	useLazyGetDriveItemFromUrlQuery,
	useLazyGetDriveItemQuery,
	useGetSharepointListsQuery,
	useLazyGetSharepointListsQuery,
	useGetListIdFromDriveQuery,
	useLazyGetListIdFromDriveQuery,
	useAddFileToSharepointMutation,
	useAddFileToSharepointBySessionMutation,
	useAddFolderToSharepointMutation,
	useRenameSharepointItemMutation,
	useMoveSharepointItemMutation,
	useDeleteSharepointItemMutation,
	useAddMetadataToFileMutation,
	useGetEventDataQuery,
	useLazyGetEventDataQuery,
	useGetUserCalendarsQuery,
	useLazyGetUserCalendarsQuery,
	useGetUserByEmailQuery,
	useLazyGetUserByEmailQuery,
	useGetUserProfileQuery,
	useLazyGetUserProfileQuery,
	useGetInboxQuery,
	useGetSentItemsQuery,
	useUpdateEventMutation,
	useCreateEventMutation,
	useSendEmailMutation,
	useDeleteEmailMutation,
	useCopySharepointItemMutation,
	useGetUserEmailsQuery,
	useReadMsgFileDataMutation,
	useBatchAddFoldersToSharepointMutation,
	useBatchAddFilesToSharepointMutation,
} = msGraphApi;
